import { OvFormInput } from '@components'
import { useStore } from '@hooks/useStore'
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { emailChangeValidationSchema } from './validation/email-change.form.validation'

interface EmailChangeFormData {
  new_email: string
  current_password: string
}

interface EmailChangeFormProps {
  open: boolean
  onClose: () => void
}

export const EmailChangeForm = observer(({ open, onClose }: EmailChangeFormProps) => {
  const { user, ui } = useStore()
  const [showSuccess, setShowSuccess] = useState(false)

  const form = useForm<EmailChangeFormData>({
    resolver: yupResolver(emailChangeValidationSchema(user.current_user?.email)),
    defaultValues: {
      new_email: '',
      current_password: '',
    },
    mode: 'onChange',
  })

  const handleClose = () => {
    form.reset()
    setShowSuccess(false)
    onClose()
  }

  const onSubmit = async (data: EmailChangeFormData) => {
    try {
      await user.requestEmailChange(data)
      setShowSuccess(true)
      form.reset()
      ui.handleToaster('Verificatie e-mail verzonden naar je nieuwe e-mailadres', {
        type: 'success',
      })
      // Close modal after 3 seconds
      setTimeout(() => {
        handleClose()
      }, 3000)
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.new_email?.[0] ||
        error?.response?.data?.errors?.current_password?.[0] ||
        error?.response?.data?.errors?.rate_limit?.[0] ||
        error?.response?.data?.errors?.pending_change?.[0] ||
        'Er is een fout opgetreden bij het wijzigen van je e-mailadres'

      ui.handleToaster(errorMessage, { type: 'error' })
    }
  }

  const FormInput = OvFormInput

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth>
      <FormProvider {...form}>
        <DialogTitle>E-mailadres wijzigen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}>
            {showSuccess && (
              <Alert
                severity="success"
                onClose={() => setShowSuccess(false)}>
                Een verificatie e-mail is verzonden naar je nieuwe e-mailadres. Klik op de link in de e-mail om de
                wijziging te bevestigen.
              </Alert>
            )}

            {!showSuccess && (
              <>
                <Alert severity="info">
                  Om je e-mailadres te wijzigen, vul je nieuwe e-mailadres in en bevestig met je huidige wachtwoord. Je
                  ontvangt een verificatie e-mail op je nieuwe adres.
                </Alert>

                <FormInput
                  label="Nieuw e-mailadres"
                  name="new_email"
                  type="email"
                />

                <FormInput
                  label="Huidig wachtwoord"
                  name="current_password"
                  type="password"
                />
              </>
            )}

            {!showSuccess && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                <Button
                  onClick={handleClose}
                  disabled={form.formState.isSubmitting || user.loading}>
                  Annuleren
                </Button>
                <Button
                  variant="contained"
                  onClick={form.handleSubmit(onSubmit)}
                  disabled={!form.formState.isValid || form.formState.isSubmitting || user.loading}>
                  Verificatie e-mail verzenden
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </FormProvider>
    </Dialog>
  )
})
